import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { LifeBuoyIcon, SparklesIcon, ArrowUpIcon, TicketIcon, CheckCircle2Icon } from 'lucide-react';
type Ticket = {
  id: string;
  question: string;
};
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  ticket?: Ticket;
};
// Lightweight support knowledge base. Each entry matches on keywords and
// returns a concise answer. Anything not matched becomes a support ticket.
const KNOWLEDGE_BASE: {
  keywords: string[];
  answer: string;
}[] = [{
  keywords: ['business hub', 'create hub', 'open hub', 'sell', 'selling'],
  answer: 'You can create a Business Hub from the Discover chat — just tell Talab you want to sell or manage inventory, and it will set up a workspace for you with a catalog, leads, and an AI assistant.'
}, {
  keywords: ['listing', 'free', 'how many', 'limit', 'add product'],
  answer: 'Your Free plan includes 1 listing. To add more, upgrade in Settings → Plan & Billing: Starter (3 listings, AED 299/mo), Growth (10, AED 599/mo), Pro (40, AED 1,199/mo) or Scale (100+, AED 5,000/mo).'
}, {
  keywords: ['upgrade', 'plan', 'billing', 'price', 'pricing', 'subscribe'],
  answer: 'Go to Business Hub → Settings → Plan & Billing and tap “Upgrade Plan”. You can compare packages and pay securely by card in the upgrade window.'
}, {
  keywords: ['shop', 'storefront', 'logo', 'link', 'handle'],
  answer: 'Your storefront lives at talab.app/<your-handle>. Set your shop logo and handle in Business Hub → Settings → Business Profile, then preview it from the “My Shop” menu item.'
}, {
  keywords: ['lead', 'leads', 'customer', 'inquiry', 'enquiry'],
  answer: 'Leads captured by your AI assistant appear in Business Hub → Leads, where you can track each buyer through your pipeline stages.'
}, {
  keywords: ['payment', 'card', 'pay', 'invoice', 'refund'],
  answer: 'Payments are handled securely by card during upgrade. You can update your payment method anytime in Settings → Plan & Billing.'
}];
const SUGGESTED = ['How do I add more listings?', 'How do I upgrade my plan?', 'How do I set up my shop link?', 'Where can I see my leads?'];
function findAnswer(text: string): string | null {
  const t = text.toLowerCase();
  let best: {
    score: number;
    answer: string;
  } | null = null;
  for (const entry of KNOWLEDGE_BASE) {
    const score = entry.keywords.reduce((acc, k) => t.includes(k) ? acc + 1 : acc, 0);
    if (score > 0 && (!best || score > best.score)) {
      best = {
        score,
        answer: entry.answer
      };
    }
  }
  return best?.answer ?? null;
}
export function Help() {
  const [query, setQuery] = useState('');
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chat, isTyping]);
  const ask = (text: string) => {
    if (!text.trim()) return;
    setChat(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const answer = findAnswer(text);
      if (answer) {
        setChat(prev => [...prev, {
          role: 'ai',
          content: answer
        }]);
      } else {
        // AI doesn't know — open a support ticket.
        const ticketId = `TLB-${Math.floor(1000 + Math.random() * 9000)}`;
        setChat(prev => [...prev, {
          role: 'ai',
          content: "I'm not certain about that one, so I've opened a support ticket for you. Our team will look into it and I'll get back to you on this shortly.",
          ticket: {
            id: ticketId,
            question: text
          }
        }]);
      }
    }, 1100);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    ask(query);
  };
  return <div className="flex-1 flex flex-col h-full bg-white relative overflow-hidden" data-id="element-2518">
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" data-id="element-2519">
        <div className="absolute top-[16%] left-1/2 -translate-x-1/2 w-[820px] h-[600px] bg-ai-purple/[0.05] blur-[140px] rounded-full" data-id="element-2520" />
        <div className="absolute bottom-[8%] right-[16%] w-[420px] h-[420px] bg-primary-indigo/[0.05] blur-[130px] rounded-full" data-id="element-2521" />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-24 relative z-10" data-id="element-2522">
        <div className="max-w-3xl mx-auto w-full h-full flex flex-col" data-id="element-2523">
          {chat.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center py-6" data-id="element-2524">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full flex flex-col items-center relative z-10" data-id="element-2525">
                <div className="w-16 h-16 lg:w-18 lg:h-18 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-slate-100 mb-5" data-id="element-2526">
                  <LifeBuoyIcon className="w-8 h-8 text-primary-indigo" data-id="element-2527" />
                </div>

                <div className="text-center space-y-2.5 mb-6" data-id="element-2528">
                  <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-2529">
                    How can we{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-2530">
                      help?
                    </span>
                  </h1>
                  <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-2531">
                    Ask our AI assistant anything about Talab. If it can't
                    answer, it'll open a support ticket and our team will get
                    back to you.
                  </p>
                </div>

                <div className="w-full max-w-2xl bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 mb-6 relative group transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]" data-id="element-2532">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-2533" />
                  <form onSubmit={handleSubmit} className="relative bg-white rounded-[22px] p-4 flex items-end gap-2" data-id="element-2534">
                    <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }} placeholder="Ask a question…" className="flex-1 resize-none outline-none text-base lg:text-lg text-slate-800 placeholder:text-slate-300 bg-transparent px-2 py-1.5 min-h-[40px]" rows={1} data-id="element-2535" />
                    <button type="submit" disabled={!query.trim()} className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple text-white flex items-center justify-center shadow-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0" aria-label="Send" data-id="element-2536">
                      <ArrowUpIcon className="w-5 h-5" data-id="element-2537" />
                    </button>
                  </form>
                </div>

                <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-2538">
                  {SUGGESTED.map(s => <button key={s} onClick={() => ask(s)} className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-100 rounded-2xl text-left text-sm text-slate-700 shadow-sm hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors" data-id="element-2539">
                      <SparklesIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-2540" />
                      <span className="truncate" data-id="element-2541">{s}</span>
                    </button>)}
                </div>
              </motion.div>
            </div> : <div className="flex-1 py-8 space-y-5" data-id="element-2542">
              <button onClick={() => setChat([])} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-primary-indigo hover:text-primary-indigo transition-colors mb-2" data-id="element-2543">
                <LifeBuoyIcon className="w-4 h-4" data-id="element-2544" /> New question
              </button>

              {chat.map((m, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-2545">
                  {m.role === 'ai' && <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3 flex-shrink-0" data-id="element-2546">
                      <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-2547" />
                    </div>}
                  <div className={`max-w-[80%] ${m.role === 'user' ? '' : 'space-y-3'}`} data-id="element-2548">
                    <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-primary-indigo text-white rounded-br-md' : 'bg-white border border-slate-100 text-slate-700 shadow-sm rounded-bl-md'}`} data-id="element-2549">
                      {m.content}
                    </div>
                    {m.ticket && <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-2xl" data-id="element-2550">
                        <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-2551">
                          <TicketIcon className="w-4 h-4 text-amber-600" data-id="element-2552" />
                        </div>
                        <div className="min-w-0" data-id="element-2553">
                          <div className="flex items-center gap-1.5 text-sm font-bold text-amber-900" data-id="element-2554">
                            <CheckCircle2Icon className="w-4 h-4 text-amber-600" data-id="element-2555" />
                            Ticket {m.ticket.id} created
                          </div>
                          <p className="text-xs text-amber-700 mt-0.5" data-id="element-2556">
                            We'll follow up with you by email about “
                            {m.ticket.question}”.
                          </p>
                        </div>
                      </div>}
                  </div>
                </motion.div>)}

              {isTyping && <div className="flex justify-start" data-id="element-2557">
                  <div className="w-9 h-9 rounded-full bg-white border border-slate-100 shadow-sm flex items-center justify-center mr-3" data-id="element-2558">
                    <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-2559" />
                  </div>
                  <div className="bg-white border border-slate-100 shadow-sm rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1" data-id="element-2560">
                    {[0, 1, 2].map(d => <motion.span key={d} className="w-1.5 h-1.5 bg-slate-300 rounded-full" animate={{
                opacity: [0.3, 1, 0.3]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                delay: d * 0.2
              }} data-id="element-2561" />)}
                  </div>
                </div>}
              <div ref={bottomRef} data-id="element-2562" />
            </div>}
        </div>
      </div>

      {chat.length > 0 && <div className="flex-shrink-0 px-4 pb-7 pt-2 relative z-20 bg-gradient-to-t from-white via-white to-transparent" data-id="element-2563">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto w-full bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 flex items-end gap-2" data-id="element-2564">
            <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }} placeholder="Ask another question…" className="flex-1 resize-none outline-none text-base text-slate-800 placeholder:text-slate-300 bg-transparent px-3 py-2.5 min-h-[44px]" rows={1} data-id="element-2565" />
            <button type="submit" disabled={!query.trim()} className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-indigo to-ai-purple text-white flex items-center justify-center shadow-sm hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0" aria-label="Send" data-id="element-2566">
              <ArrowUpIcon className="w-5 h-5" data-id="element-2567" />
            </button>
          </form>
        </div>}
    </div>;
}